import { fetchHealthStatus, resendHealthStatusMask } from "../api/healthStatus";

class HealthStatusModal {
    constructor(ModalElem = [], StatusIconElem = []) {
        if (!ModalElem.length || !StatusIconElem.length) return;
        this.ModalElem = ModalElem;
        this.StatusIconElem = StatusIconElem;
        this.endpointUrl = ModalElem.data('url');
        this.states = {};
        this.inFlight = new Set();
        this.autoBlocked = new Set();
        this.active = false;
        this.generation = 0;
        this.fresh = false;
        this.controller = new AbortController();

        ModalElem.on('show.bs.modal.healthStatus', () => this.start());
        ModalElem.on('hide.bs.modal.healthStatus', () => this.stop());
        ModalElem.on('click.healthStatus', '.health-status-resend', event => {
            event.preventDefault();
            const button = $(event.currentTarget);
            this.requestResend(String(button.attr('data-mask-id')), true);
        });
        this.pageExit = () => this.stop();
        window.addEventListener('pagehide', this.pageExit);
        window.addEventListener('beforeunload', this.pageExit);
        window.addEventListener('pageshow', event => {
            if (!event.persisted) return;
            if (this.ModalElem.hasClass('show')) this.start();
            else {
                this.controller = new AbortController();
                this.refresh();
            }
        });
        this.refresh(); // Initial header badge; automatic resends require an open modal.
    }

    start() {
        this.stop();
        this.active = true;
        this.controller = new AbortController();
        this.countdownTimer = setInterval(() => this.updateButtons(), 1000);
        this.poll();
    }

    stop() {
        this.active = false;
        this.generation++;
        this.controller.abort();
        clearTimeout(this.pollTimer);
        clearInterval(this.countdownTimer);
        this.refreshPromise = null;
        this.inFlight.clear();
        this.fresh = false;
    }

    async poll() {
        const generation = this.generation;
        const refreshed = await this.refresh();
        if (!this.active || generation !== this.generation) return;
        if (refreshed) {
            // This GET is the fresh status check before every automatic POST.
            for (const [maskId, state] of Object.entries(this.states)) {
                if (!this.active || generation !== this.generation) return;
                if (state.status === 'error' && state.requestedAt !== null &&
                    this.remaining(state) === 0 && !this.autoBlocked.has(maskId)) {
                    await this.requestResend(maskId, false);
                }
            }
        }
        if (this.active && generation === this.generation) {
            this.pollTimer = setTimeout(() => this.poll(), 12000);
        }
    }

    async refresh() {
        if (this.refreshPromise) return this.refreshPromise;
        const generation = this.generation;
        const pending = (async () => {
            try {
                const response = await fetchHealthStatus(this.endpointUrl, this.controller.signal);
                if (generation !== this.generation) return false;
                this.states = response.resends || {};
                this.csrfToken = response.csrfToken;
                this.setServerTime(this.states);
                this.ModalElem.find('.health-status-table-wrapper').html(response.data);
                this.fresh = true;
                this.feedback('');
                this.checkForErrors();
                this.updateButtons();
                return true;
            } catch (error) {
                if (generation !== this.generation) return false;
                this.fresh = false;
                this.feedback('Status nije dostupan. Ponovna provjera slijedi automatski.');
                this.updateButtons();
                return false;
            }
        })();
        this.refreshPromise = pending;
        try {
            return await pending;
        } finally {
            if (this.refreshPromise === pending) this.refreshPromise = null;
        }
    }

    async requestResend(maskId, manual) {
        if (!this.active || this.inFlight.has(maskId)) return;
        const generation = this.generation;
        this.inFlight.add(maskId);
        this.updateButtons();
        try {
            if (!(await this.refresh())) return;
            if (!this.active || generation !== this.generation) return;
            const state = this.states[maskId];
            if (!this.fresh || !state || state.status !== 'error' || this.remaining(state) > 0) return;
            if (!manual && (state.requestedAt === null || this.autoBlocked.has(maskId))) return;
            const button = this.ModalElem.find('.health-status-resend').filter((_, elem) =>
                String($(elem).attr('data-mask-id')) === maskId);
            const url = button.attr('data-resend-url');
            if (!url) return;
            const result = await resendHealthStatusMask(url, this.csrfToken, this.controller.signal);
            if (!this.active || generation !== this.generation) return;
            if (result.serverNow) {
                this.states[maskId] = { ...state, ...result };
                this.offset = result.serverNow * 1000 - Date.now();
            }
            if (result.httpStatus === 409) this.autoBlocked.add(maskId);
            else this.autoBlocked.delete(maskId);
            this.feedback(result.error || (result.httpStatus === 202 ?
                'Zahtjev za ponovno slanje je zaprimljen. Čeka se rezultat importa.' : ''));
        } catch (error) {
            if (generation !== this.generation) return;
            // Never repeat from stale metadata after an ambiguous network outcome.
            this.fresh = false;
            this.feedback(error.response?.data?.error || 'Zahtjev nije potvrđen. Čeka se nova provjera statusa.');
        } finally {
            if (generation === this.generation) {
                this.inFlight.delete(maskId);
                this.updateButtons();
            }
        }
    }

    setServerTime(states) {
        const state = Object.values(states)[0];
        if (state) this.offset = state.serverNow * 1000 - Date.now();
    }

    remaining(state) {
        if (!state.nextAllowedAt) return 0;
        return Math.max(0, Math.ceil((state.nextAllowedAt * 1000 - Date.now() - (this.offset || 0)) / 1000));
    }

    updateButtons() {
        this.ModalElem.find('.health-status-resend').each((_, elem) => {
            const button = $(elem);
            const maskId = String(button.attr('data-mask-id'));
            const state = this.states[maskId];
            const seconds = state ? this.remaining(state) : 0;
            const sending = this.inFlight.has(maskId);
            button.prop('disabled', !this.fresh || !state || seconds > 0 || sending);
            button.text(sending ? 'Slanje…' : seconds > 0 ? `Pošalji ponovno (${seconds} s)` : 'Pošalji ponovno');
        });
    }

    feedback(message) {
        this.ModalElem.find('.health-status-feedback').text(message);
    }

    checkForErrors() {
        const table = this.ModalElem.find('.health-status-table');
        const errors = Number(table.data('errors-num')) || 0;
        const warnings = Number(table.data('warn-num')) || 0;
        this.StatusIconElem.removeClass('has-errors has-warnings');
        this.StatusIconElem.find('p').text(errors || warnings || '');
        if (errors) this.StatusIconElem.addClass('has-errors');
        else if (warnings) this.StatusIconElem.addClass('has-warnings');
    }
}

export default HealthStatusModal;
